import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, FileText, Pencil, Trash2, Eye, MapPin, Calendar } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "@/components/ui/Card";
import {
  AssaultIncidentForm,
  type AssaultIncidentRecord,
  type AssaultIncidentFormData,
} from "./AssaultIncidentForm";
import { AssaultIncidentDetail } from "./AssaultIncidentDetail";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface AssaultIncidentsListProps {
  personId: string;
}

export function AssaultIncidentsList({ personId }: AssaultIncidentsListProps) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingIncident, setEditingIncident] =
    useState<AssaultIncidentRecord | null>(null);
  const [viewingIncidentId, setViewingIncidentId] = useState<number | null>(
    null,
  );

  const { data, isLoading } = useQuery<{ data: AssaultIncidentRecord[] }>({
    queryKey: ["assaultIncidents", personId],
    queryFn: () =>
      api.get<{ data: AssaultIncidentRecord[] }>(
        `/clients/${personId}/assault-incidents`,
      ),
    enabled: !!personId,
  });

  const createMutation = useMutation({
    mutationFn: (body: AssaultIncidentFormData) =>
      api.post(`/clients/${personId}/assault-incidents`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["assaultIncidents", personId],
      });
      setShowForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      ...body
    }: AssaultIncidentFormData & { id: number }) =>
      api.put(`/clients/${personId}/assault-incidents/${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["assaultIncidents", personId],
      });
      setEditingIncident(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) =>
      api.delete(`/clients/${personId}/assault-incidents/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["assaultIncidents", personId],
      });
    },
  });

  const incidents = data?.data ?? [];

  // If viewing a specific incident, show detail view
  if (viewingIncidentId !== null) {
    return (
      <AssaultIncidentDetail
        personId={personId}
        incidentId={viewingIncidentId}
        onBack={() => setViewingIncidentId(null)}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold">Assault Incidents</h3>
        <Button size="sm" className="gap-2" onClick={() => setShowForm(true)}>
          <Plus className="h-4 w-4" />
          Add Incident
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardContent className="p-4">
            <AssaultIncidentForm
              onSubmit={(d) => createMutation.mutate(d)}
              onCancel={() => setShowForm(false)}
              isLoading={createMutation.isPending}
            />
          </CardContent>
        </Card>
      )}

      {editingIncident && (
        <Card>
          <CardContent className="p-4">
            <AssaultIncidentForm
              initial={editingIncident}
              onSubmit={(d) =>
                updateMutation.mutate({ id: editingIncident.id, ...d })
              }
              onCancel={() => setEditingIncident(null)}
              isLoading={updateMutation.isPending}
            />
          </CardContent>
        </Card>
      )}

      {isLoading && (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div
              key={i}
              className="h-20 animate-pulse rounded-lg border bg-muted"
            />
          ))}
        </div>
      )}

      {!isLoading && incidents.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <FileText className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              No assault incidents found.
            </p>
          </CardContent>
        </Card>
      )}

      {!isLoading &&
        incidents.map((incident) => {
          const authorName =
            incident.author_first_name && incident.author_last_name
              ? `${incident.author_first_name} ${incident.author_last_name}`
              : null;

          const perpetratorName =
            incident.perpetrator_first_name && incident.perpetrator_last_name
              ? `${incident.perpetrator_first_name} ${incident.perpetrator_last_name}`
              : null;

          return (
            <Card key={incident.id}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-sm font-medium">
                      Incident #{incident.id}
                    </CardTitle>
                    <CardDescription className="mt-0.5">
                      {incident.incident_date
                        ? new Date(
                            incident.incident_date,
                          ).toLocaleDateString()
                        : new Date(
                            incident.created_at,
                          ).toLocaleDateString()}
                      {authorName && ` | by ${authorName}`}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setViewingIncidentId(incident.id)}
                    >
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setEditingIncident(incident)}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        if (confirm("Delete this assault incident?")) {
                          deleteMutation.mutate(incident.id);
                        }
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                  {incident.incident_date && (
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {new Date(incident.incident_date).toLocaleDateString()}
                    </span>
                  )}
                  {incident.location && (
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {incident.location}
                    </span>
                  )}
                  {perpetratorName && (
                    <span>
                      Perpetrator: {perpetratorName}
                      {incident.perpetrator_relationship &&
                        ` (${incident.perpetrator_relationship})`}
                    </span>
                  )}
                  {incident.sart_response && (
                    <span className="rounded bg-blue-100 px-1.5 py-0.5 text-blue-700">
                      SART Response
                    </span>
                  )}
                  {incident.forensic_exam && (
                    <span className="rounded bg-purple-100 px-1.5 py-0.5 text-purple-700">
                      Forensic Exam
                    </span>
                  )}
                </div>
                {incident.details && (
                  <p className="mt-2 text-sm text-foreground line-clamp-2">
                    {incident.details}
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
    </div>
  );
}
